import store from '@/store';
import { updateSiemprePlusData, setUpdated } from '@/actions/siemprePlus';
import { setUserAuthToken } from 'js-api-client';
import Cookies from 'js-cookie';
import growthBook from '../../services/growthBook';
import { setAlert } from '../../actions';
import { resetCardsState } from '../../actions/cards';
import errorMonitoring from '../../errorMonitoring';
import shouldIgnoreWidgetUpdate from './shouldIgnoreWidgetUpdate';

const SESSION_FLAG = 'FUNNEL-LOGGED';

/**
 * Update siemprePlus data in the store
 * @param {object} param0 - user, loyaltyName, error
 * @param {Object|null} param0.user - User data
 * @param {string} param0.loyaltyName - Loyalty program name
 * @param {*} [param0.error] - Set by the widget only when its profile request failed
 */
const handleDotersOrSiemprePlusProfileUpdate = ({ user, loyaltyName, error }) => {
  const {
    siemprePlus,
    whitelabelConfig: { env },
  } = store.getState();
  const { logged, requested, user: currentUser } = siemprePlus.toJS();

  // ⚠️ PROTECTION: If unified account is enabled and has user data,
  // ignore widget events that would clear the state (user === null)
  // This prevents the legacy widget from overwriting unified account data
  if (!user && shouldIgnoreWidgetUpdate()) {
    // Widget is trying to clear the state, but unified account is managing it
    // Ignore this event to prevent state loss
    return;
  }

  // With a session loaded, only an explicit sign-out closes it. The widget re-fetches the profile on
  // every purchase update (`siempreplus_refresh`); when that request fails it drops its cookie and
  // publishes `{ user: null, error }`, and every refresh after that finds no cookie and publishes
  // `{ user: null, error: null }`. Its sign-out path is the only one that publishes `error: false`.
  // Clearing the user on those events made `Purchase` detach the wallet (`PUT wallet_type: null`)
  // while the payment was in flight (MER-101), so the failure is reported once and the session kept
  // until the buyer signs out or the page reloads.
  const isExplicitSignOut = error === false;
  if (!user && currentUser && !isExplicitSignOut) {
    if (error) {
      errorMonitoring.notify({
        error: error instanceof Error ? error : new Error('Loyalty profile refresh failed'),
        info: {
          from: 'handleDotersOrSiemprePlusProfileUpdate',
          loyaltyName,
          // Separates an expired token (401) from a network or server failure.
          status: error?.status,
          widgetError: error,
        },
      });
    }
    return;
  }

  if (user) {
    setUserAuthToken(user.token);
    const loggedSession = sessionStorage.getItem(SESSION_FLAG);
    if (!logged && !loggedSession) {
      sessionStorage.setItem(SESSION_FLAG, true);
      store.dispatch(setAlert('logged_in', 'success'));
    }
  } else {
    /**
     * If the user is not logged in, we need to remove the token from the API client
     * First validate if requested and no user is present.
     * Related issues: https://reservamossaas.atlassian.net/browse/EN-875 and https://reservamossaas.atlassian.net/browse/EN-1580
     */
    if (requested) {
      setUserAuthToken(undefined);
    }
    store.dispatch(resetCardsState());
  }
  store.dispatch(updateSiemprePlusData(user, loyaltyName));
  store.dispatch(setUpdated(!!user));
  if (!Cookies.get('token')) sessionStorage.removeItem(SESSION_FLAG);

  // Set GrowthBook user attributes
  if (env.growthBook)
    growthBook.setAttributes({
      logged: !!user,
    });
};

export default handleDotersOrSiemprePlusProfileUpdate;
